import React from "react";
import { Card, Typography, Avatar, Box } from "@mui/material";
import { AiFillBulb } from "react-icons/ai";

const styles = {
    card: {
        backgroundColor: "#fef9f5",
        borderLeft: "5px solid #fb923c",
        borderRadius: "12px",
        padding: "30px",
        fontFamily: "Segoe UI",
        marginBottom: "30px",
    },
    heading: {
        display: "flex",
        alignItems: "center",
        fontSize: "1.75rem",
        fontWeight: 700,
        color: "#c2410c",
        marginBottom: "20px",
    },
    codeBlock: {
        backgroundColor: "#fff8dc",
        fontSize: "14px",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #ccc",
        marginTop: "10px",
        marginBottom: "25px",
    },
    list: {
        paddingLeft: "20px",
        marginBottom: "20px",
    },
};

export default function ExpressRestApiWithDb() {
    return (
        <Card sx={styles.card}>
            <div style={styles.heading}>
                <Avatar
                    sx={{
                        bgcolor: "#ffedd5",
                        color: "#ea580c",
                        boxShadow: "0 0 5px black",
                        mr: 2,
                    }}
                >
                    <AiFillBulb />
                </Avatar>
                Express REST API with MongoDB
            </div>

            <Typography variant="body1" paragraph>
                This example shows how to build a simple RESTful API using Express.js and connect it to a MongoDB database with Mongoose. It demonstrates user creation, search, and retrieval by ID with proper async handling and structure.
            </Typography>

            <Typography variant="h6">1️⃣ Basic Server Setup</Typography>
            <Box sx={styles.codeBlock}>
                {`const express = require('express');
const mongoose = require('mongoose');
const app = express();
const port = 3000;

app.use(express.json());

mongoose.connect('mongodb://localhost:27017/expressdb', {
  useNewUrlParser: true,
  useUnifiedTopology: true
})
.then(() => console.log('MongoDB connected'))
.catch(err => {
  console.error('MongoDB connection error:', err);
  process.exit(1);
});`}
            </Box>

            <Typography variant="h6">2️⃣ Defining the User Schema</Typography>
            <Box sx={styles.codeBlock}>
                {`const userSchema = new mongoose.Schema({
  name: { type: String, required: true },
  age: Number,
  email: { type: String, required: true, unique: true }
}, { timestamps: true });

const User = mongoose.model('User', userSchema);`}
            </Box>

            <Typography variant="h6">3️⃣ Controllers</Typography>
            <Typography variant="body2">💡 Sample endpoint logic for common routes:</Typography>
            <Box sx={styles.codeBlock}>
                {`// GET /users/:userId
const getUser = async (req, res) => {
  try {
    const user = await User.findById(req.params.userId);
    if (!user) return res.status(404).json({ message: 'User not found' });
    res.json({ success: true, user });
  } catch (error) {
    res.status(500).json({ message: error.message });
  }
};

// POST /users
const createUser = async (req, res) => {
  try {
    const newUser = new User(req.body);
    const saved = await newUser.save();
    res.status(201).json({ success: true, user: saved });
  } catch (error) {
    res.status(400).json({ message: error.message });
  }
};`}
            </Box>

            <Typography variant="h6">4️⃣ Search Endpoint</Typography>
            <Box sx={styles.codeBlock}>
                {`// GET /users/search?term=ali
const searchUsers = async (req, res) => {
  try {
    const term = req.query.term || '';
    const results = await User.find({ name: { $regex: term, $options: 'i' } });
    res.json({ success: true, results });
  } catch (error) {
    res.status(500).json({ message: error.message });
  }
};`}
            </Box>

            <Typography variant="h6">5️⃣ API Routes</Typography>
            <Box sx={styles.codeBlock}>
                {`app.get('/users/:userId', getUser);
app.post('/users', createUser);
app.get('/users/search', searchUsers);`}
            </Box>

            <Typography variant="h6">6️⃣ Global Error Handler</Typography>
            <Box sx={styles.codeBlock}>
                {`app.use((err, req, res, next) => {
  console.error('Global error handler:', err);
  res.status(500).json({ success: false, message: 'Internal Server Error' });
});`}
            </Box>

            <Typography variant="h6">7️⃣ Starting the Server</Typography>
            <Box sx={styles.codeBlock}>
                {`app.listen(port, () => {
  console.log(\`Server running on http://localhost:\${port}\`);
});`}
            </Box>
        </Card>
    );
}
